import React from 'react'

import { useIncidentSummary } from '../hooks/useIncidentSummary'
import { useLightMode } from '@/hooks/useLightMode'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import {
  AiOperatorIcon,
  Box,
  Chip,
  type ColorScheme,
  Flex,
  Modal,
  Pill,
  Text,
} from '@hivewatch/dark'

import { AiIncidentSeverityCategory } from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'
import { tVar } from '@/utils/i18n'

import {
  CATEGORY_LABEL_KEYS,
  SEVERITY_FIELD_TO_CATEGORY,
  SummarizeIncidentsModalIProps,
  SummaryPayload,
} from '../utils/incidentSummary'

const CONFIRM_MAX_W = '560px'
const RESULTS_MAX_W = '900px'
const CHIP_LIST_MAX_H = '200px'

/**
 * The design system has no severity ramp of its own, so each category borrows the status token
 * pair closest to its urgency. Token suffixes only — `tokenPrefix` prepends `light.` in light
 * mode, which is how the rest of the rebrand resolves the two colour sets.
 */
const CATEGORY_PILL_TOKENS: Record<
  AiIncidentSeverityCategory,
  { bg: string; color: string }
> = {
  [AiIncidentSeverityCategory.Critical]: {
    bg: 'status.error-bg',
    color: 'status.error-text',
  },
  [AiIncidentSeverityCategory.Urgent]: {
    bg: 'status.warning-bg',
    color: 'status.warning-text',
  },
  [AiIncidentSeverityCategory.Moderate]: {
    bg: 'status.active-bg',
    color: 'status.active-text',
  },
  [AiIncidentSeverityCategory.Low]: {
    bg: 'status.success-bg',
    color: 'status.success-text',
  },
  [AiIncidentSeverityCategory.Informational]: {
    bg: 'bg.chip-secondary',
    color: 'text.chip-secondary',
  },
}

const tokenPrefix = (colorScheme: ColorScheme) =>
  colorScheme === 'light' ? 'light.' : ''

/**
 * Rebrand variant of {@link ./SummarizeIncidentsModal.SummarizeIncidentsModal}, built on the
 * `@hivewatch/dark` `Modal`. Rendered from the rebranded incidents table
 * (`DarkIncidentsTable`); the legacy table keeps the legacy modal.
 */
export const DarkSummarizeIncidentsModal = ({
  incidents,
  isOpen,
  onClose,
}: SummarizeIncidentsModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { summaryData, isLoading, handleSummarize, handleClose } =
    useIncidentSummary({ incidents, onClose })

  const hasResults = summaryData != null

  return (
    <Modal
      colorScheme={colorScheme}
      // `title` is a plain string and `header` stacks beneath it, so the header container becomes
      // a row to seat the AI Operator mark beside the title — where the action bar entry point
      // puts it too.
      header={
        <Box
          css={{ '& svg': { width: '28px', height: '28px' } }}
          display='inline-flex'
        >
          <AiOperatorIcon />
        </Box>
      }
      headerStyleOverrides={{
        container: {
          flexDirection: 'row-reverse',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '12px',
        },
        content: { display: 'inline-flex', flexShrink: 0 },
        // Keeps the title clear of the cat-ear close button; it stays shrinkable so a long
        // translation wraps instead of pushing the icon out of the row.
        title: { pr: '40px' },
      }}
      isOpen={isOpen}
      isSaveDisabled={!hasResults && incidents.length === 0}
      isSaveLoading={isLoading}
      maxW={hasResults ? RESULTS_MAX_W : CONFIRM_MAX_W}
      // Overlay clicks and Escape route through onClose, which no-ops while generation is in
      // flight.
      onClose={handleClose}
      onSave={hasResults ? handleClose : handleSummarize}
      saveButtonLabel={
        hasResults ? t('common:close') : t('incidents:summarize')
      }
      showCancelButton={!hasResults}
      size={hasResults ? 'default' : 'compact'}
      title={t('incidents:summary_title')}
    >
      {hasResults ? (
        <SummaryResults colorScheme={colorScheme} data={summaryData} />
      ) : (
        <ConfirmationView
          colorScheme={colorScheme}
          incidents={incidents}
          isLoading={isLoading}
        />
      )}
    </Modal>
  )
}

const ConfirmationView = ({
  incidents,
  isLoading,
  colorScheme,
}: {
  incidents: Array<{ id: string; displayId: string }>
  isLoading: boolean
  colorScheme: ColorScheme
}) => {
  const { t } = useTranslation()
  const prefix = tokenPrefix(colorScheme)

  return (
    <Box display='flex' flexDirection='column' gap='12px'>
      <Text color={`${prefix}text.primary`} textStyle='body'>
        {tVar(t, 'incidents:summary_confirm', { count: incidents.length })}
      </Text>
      <Box
        borderColor={`${prefix}border.default`}
        borderWidth='1px'
        maxH={CHIP_LIST_MAX_H}
        overflowY='auto'
        px='16px'
        py='12px'
      >
        {/* `gap` on a wrapping flex row spaces both axes, so chips keep their spacing when the
            selection runs onto several lines. */}
        <Flex flexWrap='wrap' gap='8px'>
          {incidents.map((inc) => (
            <Chip
              colorScheme={colorScheme}
              key={inc.id}
              label={inc.displayId}
              variant='field'
            />
          ))}
        </Flex>
      </Box>
      {isLoading && (
        <Text color={`${prefix}text.secondary`} textStyle='caption'>
          {t('incidents:summary_generating')}
        </Text>
      )}
    </Box>
  )
}

const SummaryResults = ({
  data,
  colorScheme,
}: {
  data: SummaryPayload
  colorScheme: ColorScheme
}) => {
  const { t } = useTranslation()
  const { overview, categoryBreakdown, incidents, patterns } = data
  const prefix = tokenPrefix(colorScheme)

  return (
    <Box display='flex' flexDirection='column' gap='24px'>
      {/* Overview */}
      <Box>
        <SectionTitle colorScheme={colorScheme}>
          {t('incidents:summary_section_overview')}
        </SectionTitle>
        <Text color={`${prefix}text.primary`} mt='8px' textStyle='body'>
          {overview.narrative}
        </Text>
        <Flex flexWrap='wrap' gap='24px' mt='16px'>
          <StatItem
            colorScheme={colorScheme}
            label={t('incidents:summary_stat_total')}
            value={overview.incidentCount}
          />
          <StatItem
            colorScheme={colorScheme}
            label={t('incidents:feed_status_active')}
            value={overview.statusBreakdown.active}
          />
          <StatItem
            colorScheme={colorScheme}
            label={t('incidents:feed_status_in_progress')}
            value={overview.statusBreakdown.inProgress}
          />
          <StatItem
            colorScheme={colorScheme}
            label={t('incidents:feed_status_resolved')}
            value={overview.statusBreakdown.resolved}
          />
        </Flex>
        <Text color={`${prefix}text.secondary`} mt='12px' textStyle='caption'>
          {formatDateTime(overview.dateRange.from)} —{' '}
          {formatDateTime(overview.dateRange.to)}
        </Text>
        {overview.topFacilities.length > 0 && (
          <Text color={`${prefix}text.secondary`} mt='4px' textStyle='caption'>
            {tVar(t, 'incidents:summary_top_facilities', {
              facilities: overview.topFacilities.join(', '),
            })}
          </Text>
        )}
        {overview.topDevices.length > 0 && (
          <Text color={`${prefix}text.secondary`} mt='4px' textStyle='caption'>
            {tVar(t, 'incidents:summary_top_devices', {
              devices: overview.topDevices.join(', '),
            })}
          </Text>
        )}
      </Box>

      <SectionDivider colorScheme={colorScheme} />

      {/* Severity breakdown */}
      <Box>
        <SectionTitle colorScheme={colorScheme}>
          {t('incidents:summary_section_severity')}
        </SectionTitle>
        <Flex flexWrap='wrap' gap='8px' mt='12px'>
          {Object.entries(overview.severityBreakdown)
            .filter(([key]) => key !== '__typename')
            .map(([key, count]) => {
              const category = SEVERITY_FIELD_TO_CATEGORY[key]
              if (!category) {
                // Surfaces backend schema drift rather than silently rendering a raw field name.
                Sentry.captureMessage(
                  `Unrecognized severityBreakdown field: ${key}`
                )
              }
              return (
                <CategoryPill
                  category={category}
                  colorScheme={colorScheme}
                  key={key}
                >
                  {category ? t(CATEGORY_LABEL_KEYS[category]) : key}: {count}
                </CategoryPill>
              )
            })}
        </Flex>
      </Box>

      {/* Category breakdown */}
      {categoryBreakdown.length > 0 && (
        <>
          <SectionDivider colorScheme={colorScheme} />
          <Box>
            <SectionTitle colorScheme={colorScheme}>
              {t('incidents:summary_section_category')}
            </SectionTitle>
            <Box display='flex' flexDirection='column' gap='12px' mt='12px'>
              {categoryBreakdown.map((cb) => (
                <Box key={cb.category}>
                  <Flex align='center' gap='8px'>
                    <CategoryPill
                      category={cb.category}
                      colorScheme={colorScheme}
                    >
                      {t(CATEGORY_LABEL_KEYS[cb.category]) ?? cb.category}
                    </CategoryPill>
                    <Text color={`${prefix}text.secondary`} textStyle='caption'>
                      {cb.count} ({Math.round(cb.percentage)}%)
                    </Text>
                  </Flex>
                  {cb.narrative && (
                    <Text
                      color={`${prefix}text.secondary`}
                      mt='4px'
                      textStyle='caption'
                    >
                      {cb.narrative}
                    </Text>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}

      {/* Patterns */}
      {patterns.length > 0 && (
        <>
          <SectionDivider colorScheme={colorScheme} />
          <Box>
            <SectionTitle colorScheme={colorScheme}>
              {t('incidents:summary_section_patterns')}
            </SectionTitle>
            <Box
              as='ul'
              display='flex'
              flexDirection='column'
              gap='8px'
              // The global reset strips list markers, so the bullet is asked for explicitly.
              listStyleType='disc'
              mt='12px'
              pl='20px'
            >
              {patterns.map((p) => (
                <Text
                  as='li'
                  color={`${prefix}text.primary`}
                  key={p.description}
                  textStyle='body'
                >
                  {p.description}
                </Text>
              ))}
            </Box>
          </Box>
        </>
      )}

      {/* Per-incident summaries */}
      {incidents.length > 0 && (
        <>
          <SectionDivider colorScheme={colorScheme} />
          <Box>
            <SectionTitle colorScheme={colorScheme}>
              {t('incidents:summary_section_incidents')}
            </SectionTitle>
            <Box display='flex' flexDirection='column' gap='16px' mt='12px'>
              {incidents.map((inc) => (
                <Box key={inc.incidentId}>
                  <Flex align='center' gap='8px' mb='4px'>
                    <Text color={`${prefix}text.primary`} textStyle='body-bold'>
                      {inc.displayId}
                    </Text>
                    <CategoryPill
                      category={inc.assignedCategory}
                      colorScheme={colorScheme}
                    >
                      {t(CATEGORY_LABEL_KEYS[inc.assignedCategory]) ??
                        inc.assignedCategory}
                    </CategoryPill>
                  </Flex>
                  <Text color={`${prefix}text.primary`} textStyle='body'>
                    {inc.summary}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

const CategoryPill = ({
  category,
  children,
  colorScheme,
}: {
  category?: AiIncidentSeverityCategory
  children: React.ReactNode
  colorScheme: ColorScheme
}) => {
  const prefix = tokenPrefix(colorScheme)
  const tokens =
    CATEGORY_PILL_TOKENS[category] ??
    CATEGORY_PILL_TOKENS[AiIncidentSeverityCategory.Informational]

  return (
    <Pill bg={`${prefix}${tokens.bg}`} color={`${prefix}${tokens.color}`}>
      {children}
    </Pill>
  )
}

const SectionDivider = ({ colorScheme }: { colorScheme: ColorScheme }) => (
  <Box
    borderColor={
      colorScheme === 'light' ? 'light.border.default' : 'border.default'
    }
    borderTopWidth='1px'
    w='100%'
  />
)

const SectionTitle = ({
  children,
  colorScheme,
}: {
  children: React.ReactNode
  colorScheme: ColorScheme
}) => (
  <Text
    color={colorScheme === 'light' ? 'light.text.primary' : 'text.primary'}
    textStyle='heading'
  >
    {children}
  </Text>
)

const StatItem = ({
  label,
  value,
  colorScheme,
}: {
  label: string
  value: number
  colorScheme: ColorScheme
}) => {
  const prefix = tokenPrefix(colorScheme)
  return (
    <Box display='flex' flexDirection='column' gap='2px'>
      <Text color={`${prefix}text.primary`} textStyle='heading'>
        {value}
      </Text>
      <Text color={`${prefix}text.secondary`} textStyle='caption'>
        {label}
      </Text>
    </Box>
  )
}
